import { DataTableSkeleton, InlineNotification } from '@carbon/react';
import { AllocationManager } from 'components/allocation-manager/allocation-tracts-table';
import { AllocationManagerHeaderActions } from 'components/allocation-manager/allocation-tracts-table/components/allocation-manager-header-actions';
import { TractNRI } from 'components/nri-table/types';
import { Section } from 'components/section';
import { atom, useAtom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { useEffect, useMemo, useState } from 'react';
import {
  AllocationManagerResponse,
  AllocationManagerVersionsResponse,
  WellsResponse,
} from 'types/api-responses';
import { formatResource } from 'utils/misc';
import { createdSubtractGeometryLoadableAtom } from './atoms';
import style from './nri-review-allocation-manager.module.scss';
import { NriReviewMap } from './nri-review-map';
import { ParticipationFactorDropdown } from './participation-factor-dropdown';

interface NriReviewAllocationManagerProps {
  selectedRow: TractNRI | undefined;
}

const LoadingAllocationManager = () => (
  <DataTableSkeleton
    rowCount={5}
    columnCount={5}
    headers={[
      { header: 'Tract' },
      { header: 'Acreage' },
      { header: 'Factor' },
      { header: 'GIS Acreage' },
      { header: 'Status' },
    ]}
  />
);

export const NriReviewAllocationManager = ({
  selectedRow,
}: NriReviewAllocationManagerProps) => {
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [selectedTractId, setSelectedTractId] = useState<number | null>(null);
  const subtractGeometryLoadable = useAtomValue(
    createdSubtractGeometryLoadableAtom
  );
  const subtractGeometry =
    subtractGeometryLoadable.state === 'hasData'
      ? (subtractGeometryLoadable.data?.geojson as
          GeoJSON.Polygon | GeoJSON.MultiPolygon | null | undefined)
      : null;

  // Get dynamic values from selected row
  const allocationId = selectedRow?.allocationManagerId ?? null;
  const devAreaId = selectedRow?.devAreaId ?? '';
  const wellId = selectedRow?.wellId ?? '';

  // Reset local state when the selected row changes
  useEffect(() => {
    setSelectedVersion('');
    setSelectedTractId(null);
  }, [allocationId, wellId]);

  // Only fetch if we have allocationId
  const allocationManagerAtom = useMemo(() => {
    if (!allocationId || !selectedVersion) {
      return atom<AllocationManagerResponse | null>(null);
    }
    return createResourceAtom<AllocationManagerResponse>(
      `/allocation_managers/${allocationId}?version=${selectedVersion}`
    );
  }, [allocationId, selectedVersion]);
  const [allocationManagerResult] = useAtom(allocationManagerAtom);

  const allocationManager = !allocationManagerResult
    ? null
    : ('loading' in allocationManagerResult &&
          allocationManagerResult.loading) ||
        !('data' in allocationManagerResult) ||
        !allocationManagerResult.data ||
        'error' in allocationManagerResult.data
      ? null
      : allocationManagerResult.data;

  // Fetch Allocation Manager Versions (only if we have allocationId)
  const versionsAtom = useMemo(() => {
    if (!allocationId) {
      return atom<AllocationManagerVersionsResponse | null>(null);
    }
    return createResourceAtom<AllocationManagerVersionsResponse>(
      `/allocation_managers/${allocationId}/versions`
    );
  }, [allocationId]);
  const allocationVersionsResult = useAtomValue(versionsAtom);

  const versions = useMemo(() => {
    if (!allocationVersionsResult || !allocationId) return [];
    if (
      ('loading' in allocationVersionsResult &&
        allocationVersionsResult.loading) ||
      !('data' in allocationVersionsResult) ||
      !allocationVersionsResult.data ||
      'error' in allocationVersionsResult.data
    ) {
      return [];
    }
    return allocationVersionsResult.data.versions;
  }, [allocationVersionsResult, allocationId]);

  const wellAtom = useMemo(() => {
    if (!wellId) {
      return atom<{ loading: false; data: null }>({
        loading: false,
        data: null,
      });
    }
    return createResourceAtom<WellsResponse>(`/wells/${wellId}`);
  }, [wellId]);
  const wellResource = useAtomValue(wellAtom);
  const { data: wellData } = formatResource(wellResource);

  const latestAllocationManagerVersion = versions?.length
    ? versions[versions.length - 1]
    : null;

  const filteredVersions = useMemo(() => {
    return versions?.slice().reverse() || []; // Reverse to show newest first
  }, [versions]);

  useEffect(() => {
    if (filteredVersions?.length > 0) {
      return setSelectedVersion(filteredVersions[0]);
    }
  }, [filteredVersions]);

  const validAllocationManager =
    !allocationManager || 'error' in allocationManager
      ? undefined
      : allocationManager;

  const allocationManagerLineItemsLoaded =
    !allocationId ||
    (allocationManagerResult &&
      'loading' in allocationManagerResult &&
      !allocationManagerResult.loading);

  return (
    <div className={style.allocationManagerSection}>
      <div className={style.allocationManagerContainer}>
        {/* Allocation Manager Table - always render section */}
        <div className={style.tableContainer}>
          <Section
            header="Allocation Manager"
            headerActions={
              validAllocationManager && wellData ? (
                <AllocationManagerHeaderActions
                  key={`allocation-header-${wellId}`}
                  allocationManager={validAllocationManager}
                  latestAllocationManagerVersion={
                    latestAllocationManagerVersion
                  }
                  wellData={wellData}
                  versions={filteredVersions}
                  selectedVersion={selectedVersion}
                  onVersionChange={setSelectedVersion}
                  supportingFilesHref={
                    wellData?.resources?.supportingFiles?.href || ''
                  }
                  developmentAreaId={devAreaId}
                  disableActions={true}
                  viewOnlySourceDocument={true}
                  hasAllocationManager={false}
                />
              ) : null
            }
          >
            {!allocationManagerLineItemsLoaded ? (
              <LoadingAllocationManager />
            ) : validAllocationManager ? (
              <>
                <AllocationManager
                  allocationManagerData={validAllocationManager}
                  onLineItemClick={setSelectedTractId}
                  selectedTractId={selectedTractId}
                  selectedVersion={selectedVersion}
                />
                <ParticipationFactorDropdown />
              </>
            ) : (
              <>
                <div className={style.allocationManagerTableContainer}>
                  <InlineNotification
                    kind="info"
                    title="No data is available at this time."
                    lowContrast
                    subtitle="Please contact a GIS team for assistance"
                    hideCloseButton
                  />
                </div>
                <ParticipationFactorDropdown />
              </>
            )}
          </Section>
        </div>

        {/* Map - only render if devAreaId is available */}
        {devAreaId ? (
          <div className={style.allocationMapContainer}>
            {allocationManagerLineItemsLoaded ? (
              <NriReviewMap
                devAreaId={devAreaId}
                wellId={wellId}
                subtractGeometry={subtractGeometry ?? null}
                allocationManager={validAllocationManager}
                selectedTractId={selectedTractId}
              />
            ) : (
              <div className={style.map}>Loading map...</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
